import { useState, useEffect, useRef, useMemo } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import { endOfDay, startOfDay, subDays } from "date-fns";
import { normalizeFirestoreDate } from "../../../lib/firestoreDate";
import { mapReportHealthDocsToChartRows } from "../lib/healthSeriesUtils";

const DEFAULT_DAYS = 90;

/**
 * Last N days of reportHealth for charts / summary (same collection as Activity calendar).
 * @param {{ clientId: string, organizationId: string, days?: number }} options
 * @returns {{ chartRows: object[], rawDocs: object[], loading: boolean, error: Error|null }}
 */
export function useClientHealthSeries({ clientId, organizationId, days = DEFAULT_DAYS }) {
  const [rawDocs, setRawDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const unsubscribeRef = useRef(null);

  const isValid = Boolean(clientId && organizationId);

  const { start, end } = useMemo(() => {
    const now = new Date();
    return {
      start: startOfDay(subDays(now, days)),
      end: endOfDay(now),
    };
  }, [days]);

  useEffect(() => {
    if (!isValid) {
      setRawDocs([]);
      setLoading(true);
      setError(null);
      return;
    }
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
    }
    setLoading(true);
    setError(null);

    const ref = firebase
      .firestore()
      .collection("organizations")
      .doc(organizationId)
      .collection("clients")
      .doc(clientId)
      .collection("reportHealth")
      .where("createdOn", ">=", start)
      .where("createdOn", "<=", end)
      .orderBy("createdOn", "asc");

    unsubscribeRef.current = ref.onSnapshot(
      (snapshot) => {
        const list = snapshot.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            ...d,
            createdOn: normalizeFirestoreDate(d.createdOn),
          };
        });
        setRawDocs(list);
        setLoading(false);
      },
      (err) => {
        setError(err);
        setRawDocs([]);
        setLoading(false);
      }
    );

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [clientId, organizationId, start, end, isValid]);

  const chartRows = useMemo(() => mapReportHealthDocsToChartRows(rawDocs), [rawDocs]);

  return { chartRows, rawDocs, loading, error };
}
